import { useState, useRef, useEffect } from 'react';

export interface ScrollableSelectOption {
  value: string;
  label: string;
}

interface ScrollableSelectProps {
  options: ScrollableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

const LIST_MAX_HEIGHT = 280; // ~8–10 itens visíveis, scroll para o resto

export function ScrollableSelect({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  loading = false,
  id,
  className = '',
  'aria-label': ariaLabel,
}: ScrollableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const baseTriggerClass =
    'w-full px-3 py-2 border rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-left text-sm flex items-center justify-between gap-2 ' +
    'border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-colors';
  const disabledClass = disabled || loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled || loading}
        disabled={disabled || loading}
        onClick={() => !disabled && !loading && setIsOpen((prev) => !prev)}
        className={`${baseTriggerClass} ${disabledClass} ${className}`.trim()}
      >
        <span className="truncate">{loading ? 'Carregando...' : displayLabel}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-50 w-full mt-1 py-1 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
          style={{ maxHeight: LIST_MAX_HEIGHT }}
        >
          {options.length === 0 && (
            <li role="option" className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {loading ? 'Carregando...' : 'Nenhuma opção'}
            </li>
          )}
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(option.value);
                  }
                }}
                className={`px-3 py-2 text-sm cursor-pointer truncate flex items-center justify-between gap-2 ${
                  isSelected
                    ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                    : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && (
                  <span className="flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden>
                    ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
